import axios from 'axios';
import { getJson, sendJson } from '@/lib/api';
import { authHeader } from '@/lib/events';
import type { WorkspaceHeader } from '@/lib/workspace';

/**
 * Campaign inquiries from BCE's side. The campaign, its events and its
 * stage are the portal's own record; BCE reads the same view the client
 * does, and adds the proposal step.
 */

export const CAMPAIGN_STAGE = {
  draft: 'DRAFT',
  inquiry: 'INQUIRY',
  proposal: 'PROPOSAL',
  agreement: 'AGREEMENT',
  planning: 'PLANNING',
  delivery: 'DELIVERY',
  reporting: 'REPORTING',
  closed: 'CLOSED',
} as const;
export type CampaignStage = (typeof CAMPAIGN_STAGE)[keyof typeof CAMPAIGN_STAGE];

export const CAMPAIGN_STAGE_LABELS: Readonly<Record<CampaignStage, string>> = {
  DRAFT: 'Draft',
  INQUIRY: 'Inquiry',
  PROPOSAL: 'Proposal',
  AGREEMENT: 'Agreement',
  PLANNING: 'Planning',
  DELIVERY: 'Delivery',
  REPORTING: 'Reporting',
  CLOSED: 'Closed',
};

export interface StaffCampaignRow {
  readonly id: string;
  readonly name: string;
  readonly organization: { readonly id: string; readonly name: string; readonly slug: string };
  readonly stage: CampaignStage;
  readonly submittedAt: string | null;
  readonly events: number;
  readonly inquiries: number;
  readonly combinedGroupSize: number;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
}

export interface CampaignEvent {
  readonly id: string;
  readonly workspaceId: string;
  readonly stage: 'INQUIRY' | 'EVENT';
  readonly teamId: string | null;
  readonly teamName: string | null;
  readonly workspace: WorkspaceHeader & {
    readonly reference?: string | null;
    readonly programName?: string | null;
    readonly city?: string | null;
    readonly state?: string | null;
    readonly venueName?: string | null;
    readonly startAt?: string | null;
    readonly estimatedParticipants?: number | null;
    readonly finalParticipants?: number | null;
    readonly status?: string | null;
    readonly eventId?: string | null;
  };
}

export interface ProposalDocument {
  readonly id: string;
  readonly name: string;
  readonly size: number | null;
  readonly uploadedAt: string;
}

export interface Proposal {
  readonly sentAt: string | null;
  readonly note: string | null;
  readonly sentByName: string | null;
  readonly documents: readonly ProposalDocument[];
}

export interface CampaignDetail {
  readonly campaign: {
    readonly _id: string;
    readonly name: string;
    readonly objective?: string;
    readonly theme?: string;
    readonly approvedMessaging?: string;
    readonly approvedProgram?: string;
    readonly participatingTeamIds?: readonly string[];
    readonly deliveryWindowStart?: string;
    readonly deliveryWindowEnd?: string;
    readonly executiveSponsor?: string;
    readonly csrGoals?: string;
    readonly reportingRequirements?: string;
    readonly closedReason?: string;
    readonly createdAt: string;
  };
  readonly stage: CampaignStage;
  readonly submission: { readonly at: string; readonly byName: string | null; readonly note: string | null } | null;
  /** The client's answers to the proposal, oldest first: go ahead, or changes asked for. */
  readonly proposalResponses: readonly {
    readonly decision: 'GO_AHEAD' | 'CHANGES';
    readonly note: string | null;
    readonly at: string;
    readonly byName: string | null;
  }[];
  readonly proposal: { readonly sentAt: string; readonly note: string | null; readonly documents: number } | null;
  readonly agreement: { readonly contractId: string; readonly status: string } | null;
  readonly checklistProgress: { readonly total: number; readonly settled: number } | null;
  readonly program: { readonly id: string; readonly name: string } | null;
  readonly events: readonly CampaignEvent[];
  readonly rollup: {
    readonly events: number;
    readonly inquiries: number;
    readonly locations: number;
    readonly combinedGroupSize: number;
    readonly windowStart: string | null;
    readonly windowEnd: string | null;
  };
  readonly organization: { readonly id: string; readonly name: string; readonly slug: string };
  readonly proposalDetail: Proposal;
  readonly portalUrl: string;
}

export const listCampaigns = (filter: { organizationId?: string; includeDrafts?: boolean }, signal?: AbortSignal): Promise<StaffCampaignRow[]> =>
  getJson<StaffCampaignRow[]>('/campaigns', { ...(filter.organizationId ? { organizationId: filter.organizationId } : {}), ...(filter.includeDrafts ? { includeDrafts: '1' } : {}) }, signal);

export const readCampaign = (campaignId: string, signal?: AbortSignal): Promise<CampaignDetail> =>
  getJson<CampaignDetail>(`/campaigns/${campaignId}`, undefined, signal);

export const readProposal = (campaignId: string, signal?: AbortSignal): Promise<Proposal> =>
  getJson<Proposal>(`/campaigns/${campaignId}/proposal`, undefined, signal);

/** One PDF onto the proposal; call once per file. */
export async function addProposalDocument(campaignId: string, file: File): Promise<ProposalDocument> {
  const form = new FormData();
  form.append('file', file);
  const response = await axios.post<{ data: ProposalDocument }>(`/api/v3/campaigns/${campaignId}/proposal/documents`, form, {
    headers: await authHeader(),
  });
  return response.data.data;
}

export const removeProposalDocument = (campaignId: string, attachmentId: string): Promise<null> =>
  sendJson<null>('delete', `/campaigns/${campaignId}/proposal/documents/${attachmentId}`);

export const sendProposal = (campaignId: string, note: string): Promise<Proposal> =>
  sendJson<Proposal>('post', `/campaigns/${campaignId}/proposal/send`, note.trim() ? { note: note.trim() } : {});

export const closeCampaign = (campaignId: string, reason: string): Promise<CampaignDetail> =>
  sendJson<CampaignDetail>('post', `/campaigns/${campaignId}/close`, { reason });
